import { Box, Grid, HStack, Icon } from '@chakra-ui/react';
import { LuBellOff } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip';
import ShortcutTooltip from '@/components/common/review/ShortcutTooltip';
import type { PlanNodeOut } from '@/client';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { PlanPlacements, type PlanPlacement } from '@/components/maintenance/model/planVocabulary';
import { DismissReasonButton } from '@/components/common/review/DismissReasonMenu';
import KeepOnScheduleButton from '@/components/maintenance/review/KeepOnScheduleButton';
import type { DismissDetail } from '@/components/maintenance/review/reviewQueueState';

interface ProposalVerdictActionsProps {
	node: PlanNodeOut;
	onKeep: (placement: PlanPlacement) => void;
	/** Absent where the card advertises no way to be turned down, in which case no control is drawn. */
	onDismiss?: (detail?: DismissDetail) => void;
	dismissLabel: string;
	isDismissOpen: boolean;
	dismissFormId: string;
	onDismissOpenChange: (open: boolean) => void;
	isPending?: boolean;
}

/**
 * The three answers a proposal takes: discard it, keep it, or keep it as a priority.
 *
 * Keeping is two buttons rather than one with a menu behind it: whether a card is a priority is the
 * second half of the decision, not a detail of it, and a review pass is a sequence of one press per
 * card. A menu would make the common case two.
 *
 * Prioritising is withheld from a card with no due date. The endpoint refuses that election with a
 * 422, since a reminder counts back from a date and there is none, so offering it would be offering
 * a button that cannot work. The tooltip says why rather than leaving a dead control unexplained.
 */
const ProposalVerdictActions = ({
	node,
	onKeep,
	onDismiss,
	dismissLabel,
	isDismissOpen,
	dismissFormId,
	onDismissOpenChange,
	isPending = false,
}: ProposalVerdictActionsProps) => {
	// Turning the card down sits apart from keeping it. The two keep buttons are variations on one
	// answer and belong together; dismiss is the other answer, and the gap between them is what says
	// so — three evenly spaced buttons read as three peers and make the row a menu to scan rather
	// than a decision with two sides.
	//
	// A phone stacks them instead. Three across a 360px screen leaves about 110px each, and a verdict
	// squeezed to fit that stops saying what it does. Stacked, each keeps its name and its bell.
	// Desktop keeps the one row, and the larger gap between dismissing and the two ways of keeping.
	//
	// On mobile the group wrapper becomes `display: contents`, so all three buttons are direct items
	// in one grid rather than nested flex shares — Discard taking one share and both keeps sharing
	// another made two of them half the width of the third.
	return (
		<Grid
			templateColumns={{ base: 'minmax(0, 1fr)', md: 'auto auto' }}
			columnGap={{ md: 6 }}
			rowGap={{ base: 2, md: 0 }}
			alignItems="center"
			justifyContent="space-between"
			w="full"
		>
			{onDismiss ? (
				<Tooltip
					content={<ShortcutTooltip action="Discard task" shortcutKey="D" />}
					// Withheld while the form is open: the button is then the form's own close control,
					// sat directly above the thing it opened, and a hint about the key that opens it is
					// describing the press the reader already made.
					disabled={isDismissOpen}
					openDelay={300}
				>
					{/* `DismissReasonButton` forwards no ref, so `asChild` has nothing to attach the
					    trigger to and the button stops responding. The span is what the tooltip holds. */}
					<Box as="span" display="inline-flex" w={{ base: 'full', md: 'auto' }} minW={0}>
						<DismissReasonButton
							label={dismissLabel}
							isOpen={isDismissOpen}
							controls={dismissFormId}
							onOpen={() => onDismissOpenChange(!isDismissOpen)}
							size="sm"
							w={{ base: 'full', md: 'auto' }}
							aria-keyshortcuts="D"
							// `visual.outline` floors these at 96px, which is right where a row holds two of
							// them and wrong where it holds three: three floors plus the gaps overrun a
							// 360px screen, and the overrun lands on whichever button shares a nesting level
							// with Schedule. Content is the floor there instead.
							verdictRow
							disabled={isPending}
						/>
					</Box>
				</Tooltip>
			) : null}

			{/* Pushed right on its own when the card advertises no way to be turned down, so keeping
			    stays where the eye already is rather than sliding to the left edge. */}
			<HStack
				display={{ base: 'contents', md: 'flex' }}
				gap={2}
				gridColumn={{ md: '2' }}
				justifySelf={{ md: 'end' }}
			>
				{/* Carries Discard's treatment: the two are the same weight of answer, and only
				    Schedule is emphasised. */}
				<Tooltip content={<ShortcutTooltip action="Keep task" shortcutKey="K" />} openDelay={300}>
					<OutlinedButton
						variant="outline"
						elevated
						size="sm"
						w={{ base: 'full', md: 'auto' }}
						verdictRow
						bg="surface.default"
						color="primary"
						borderColor="border.default"
						_hover={{ bg: 'surface.subtle', color: 'primary', borderColor: 'border.default' }}
						_active={{ bg: 'surface.subtle', color: 'primary', borderColor: 'border.default' }}
						gap={1.5}
						onClick={() => onKeep(PlanPlacements.General)}
						disabled={isPending}
						aria-keyshortcuts="K"
					>
						{/* The struck bell is what distinguishes this from Schedule beside it: both keep the
						    card, and reminders are the whole of the difference. */}
						Keep unscheduled
						<Icon as={LuBellOff} boxSize={4} />
					</OutlinedButton>
				</Tooltip>

				<KeepOnScheduleButton
					node={node}
					onKeep={() => onKeep(PlanPlacements.Schedule)}
					width={{ base: 'full', md: 'auto' }}
					inVerdictRow
					isPending={isPending}
					shortcutKey="S"
				/>
			</HStack>
		</Grid>
	);
};

export default ProposalVerdictActions;
